import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import LineStyle from "@mui/icons-material/LineStyle";
import LineWeight from "@mui/icons-material/LineWeight";
import {
  Box,
  Button,
  ClickAwayListener,
  FormControlLabel,
  IconButton,
  Paper,
  Popper,
  Radio,
  RadioGroup,
  Slider,
  Stack,
} from "@mui/material";
import { $setSelection } from "lexical";
import { useCallback, useEffect, useRef, useState } from "react";
import { ColourPicker } from "../../../generic/components/colour-picker/colour-picker";

import type { PopoverController } from "../../../../utils/hooks/use-popover";
import type { DividerTemplate } from "../../../email-design/types/email-divider-settings";
import type { ChangeDividerStyle } from "../types/divider-style";
import type { ClickAwayListenerProps } from "@mui/material";
import type { FC, MouseEvent, ReactNode } from "react";

const MIN_DIVIDER_WIDTH = 1;
const MAX_DIVIDER_WIDTH = 10;

const dividerStyleOptions: { label: string; value: DividerTemplate["style"] }[] = [
  { label: "Solid", value: "solid" },
  { label: "Dashed", value: "dashed" },
  { label: "Dotted", value: "dotted" },
];

type DividerPopup = "colour" | "width" | "style";

type ClickAway = ClickAwayListenerProps["onClickAway"];

/** Which of the toolbar's popups is open, as a controller for each. Opening one closes the other. */
function useDividerPopups(): (popup: DividerPopup) => PopoverController {
  const [openPopup, setOpenPopup] = useState<DividerPopup | null>(null);

  // Pressing a popup's controls keeps focus in the editor, so listen for Escape on the document.
  useEffect(() => {
    if (!openPopup) {
      return undefined;
    }
    const closeOnEscape = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        setOpenPopup(null);
      }
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [openPopup]);

  return (popup) => ({
    open: openPopup === popup,
    handleOpen: () => setOpenPopup(popup),
    // Only close this popup, in case another was opened by the same click.
    handleClose: () => setOpenPopup((current) => closePopup(current, popup)),
    handleToggle: () => setOpenPopup((current) => togglePopup(current, popup)),
  });
}

function closePopup(current: DividerPopup | null, popup: DividerPopup): DividerPopup | null {
  if (current === popup) {
    return null;
  }
  return current;
}

function togglePopup(current: DividerPopup | null, popup: DividerPopup): DividerPopup | null {
  if (current === popup) {
    return null;
  }
  return popup;
}

/**
 * The popups do not block the page, so a press on another toolbar button reaches that button and
 * switches popup. A press anywhere else closes the popup, and outside the editor also deselects
 * the divider, since the editor only moves its selection for presses inside it.
 */
function useClickAway(toolbarRef: React.RefObject<HTMLElement | null>): ClickAway {
  const [editor] = useLexicalComposerContext();

  return useCallback(
    (event) => {
      const { target } = event;
      if (!(target instanceof Node) || toolbarRef.current?.contains(target)) {
        return;
      }
      if (editor.getRootElement()?.contains(target)) {
        return;
      }
      editor.update(() => {
        $setSelection(null);
      });
    },
    [editor, toolbarRef],
  );
}

/**
 * The toolbar sits inside the editor, so pressing it would move the caret and deselect the divider.
 * Presses in the popups bubble here too, but a text input still needs the press to take focus.
 */
function keepDividerSelected(event: MouseEvent): void {
  if (event.target instanceof HTMLInputElement && event.target.type === "text") {
    return;
  }
  event.preventDefault();
}

export const DividerStyleToolbar: FC<{
  dividerStyle: DividerTemplate;
  onChange: ChangeDividerStyle;
}> = ({ dividerStyle, onChange }) => {
  const toolbarRef = useRef<HTMLDivElement>(null);
  const popupFor = useDividerPopups();
  const onClickAway = useClickAway(toolbarRef);

  return (
    <Stack
      ref={toolbarRef}
      onMouseDown={keepDividerSelected}
      direction="row"
      spacing={0}
      sx={{
        position: "absolute",
        bottom: "100%",
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 1,
        alignItems: "center",
        backgroundColor: "background.paper",
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
        boxShadow: 1,
        p: 0.5,
      }}
    >
      <DividerColourControl
        colour={dividerStyle.color}
        onChange={onChange}
        popover={popupFor("colour")}
        onClickAway={onClickAway}
      />
      <DividerWidthControl
        width={dividerStyle.width}
        onChange={onChange}
        popover={popupFor("width")}
        onClickAway={onClickAway}
      />
      <DividerLineStyleControl
        lineStyle={dividerStyle.style}
        onChange={onChange}
        popover={popupFor("style")}
        onClickAway={onClickAway}
      />
    </Stack>
  );
};

interface PopupProps {
  popover: PopoverController;
  onClickAway: ClickAway;
}

const DividerColourControl: FC<
  PopupProps & {
    colour: string;
    onChange: ChangeDividerStyle;
  }
> = ({ colour, onChange, popover, onClickAway }) => {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // The picker re-sends its colour whenever this callback changes, and again on close, so only
  // save a real change: that keeps the callback from looping and the template colour unpinned.
  const handleColourChange = useCallback(
    (color: string, skipHistory?: boolean) => {
      if (color === colour) {
        return;
      }
      onChange({ color }, skipHistory ?? false);
    },
    [colour, onChange],
  );

  return (
    <>
      <Button
        data-analytics-id="lexical-divider-style-colour"
        ref={anchorRef}
        onClick={popover.handleToggle}
        size="small"
        sx={{
          color: "text.secondary",
          fontWeight: "fontWeightRegular",
          px: 1,
          py: 0.5,
          "& .MuiButton-endIcon": { ml: 0.5 },
        }}
        endIcon={
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: 0.5,
              border: 1,
              borderColor: "divider",
              backgroundColor: colour,
            }}
          />
        }
      >
        Color
      </Button>
      <ColourPicker
        anchorRef={anchorRef}
        popover={popover}
        onClickAway={onClickAway}
        colour={colour}
        onChange={handleColourChange}
        inputRef={inputRef}
      />
    </>
  );
};

const DividerWidthControl: FC<
  PopupProps & {
    width: number;
    onChange: ChangeDividerStyle;
  }
> = ({ width, onChange, popover, onClickAway }) => (
  <DividerMenuButton
    popover={popover}
    onClickAway={onClickAway}
    label="Divider width"
    analyticsId="width"
    icon={<LineWeight fontSize="small" />}
  >
    <Slider
      aria-label="Divider width"
      value={width}
      onChange={(_, value) => onChange({ width: value }, true)}
      onChangeCommitted={(_, value) => onChange({ width: value }, false)}
      min={MIN_DIVIDER_WIDTH}
      max={MAX_DIVIDER_WIDTH}
      step={1}
      valueLabelFormat={(value) => `${value}px`}
      valueLabelDisplay="auto"
      sx={{ mx: 2, width: 160 }}
    />
  </DividerMenuButton>
);

const DividerLineStyleControl: FC<
  PopupProps & {
    lineStyle: DividerTemplate["style"];
    onChange: ChangeDividerStyle;
  }
> = ({ lineStyle, onChange, popover, onClickAway }) => (
  <DividerMenuButton
    popover={popover}
    onClickAway={onClickAway}
    label="Divider style"
    analyticsId="line-style"
    icon={<LineStyle fontSize="small" />}
  >
    <RadioGroup value={lineStyle} sx={{ px: 2 }}>
      {dividerStyleOptions.map((option) => (
        <FormControlLabel
          key={option.value}
          value={option.value}
          control={<Radio size="small" />}
          label={option.label}
          onChange={() => onChange({ style: option.value }, false)}
        />
      ))}
    </RadioGroup>
  </DividerMenuButton>
);

const DividerMenuButton: FC<
  PopupProps & {
    label: string;
    analyticsId: string;
    icon: ReactNode;
    children: ReactNode;
  }
> = ({ label, analyticsId, icon, children, popover, onClickAway }) => {
  const anchorRef = useRef<HTMLButtonElement>(null);

  const handleClickAway: ClickAway = (event) => {
    popover.handleClose();
    onClickAway(event);
  };

  return (
    <>
      <IconButton
        data-analytics-id={`lexical-divider-style-${analyticsId}`}
        aria-label={label}
        ref={anchorRef}
        onClick={popover.handleToggle}
        size="small"
        sx={{ color: "text.secondary", p: 0.75 }}
      >
        {icon}
      </IconButton>
      <Popper
        // oxlint-disable-next-line react/refs -- MUI anchorEl needs the DOM node; the popper only opens after the anchor has mounted
        anchorEl={anchorRef.current}
        open={popover.open}
        placement="bottom-start"
        sx={{ zIndex: "modal" }}
      >
        <ClickAwayListener onClickAway={handleClickAway}>
          <Paper elevation={8} sx={{ py: 1 }}>
            {children}
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
};
